import { motion } from 'framer-motion';
import { useDiagnosticStore } from '@shared/state';
import { anchorItemsForMode } from '@shared/diagnostic/anchor-items';

/** Roughly how long a learner spends on one tappable item. Sets the "time left". */
const SECONDS_PER_ITEM = 9;

function minutesLeft(remaining: number): string {
  const mins = Math.ceil((remaining * SECONDS_PER_ITEM) / 60);
  if (remaining <= 0) return 'Done';
  if (mins <= 1) return 'Under a minute left';
  return `About ${mins} min left`;
}

/**
 * The progress strip. Full width, directly under the chat header, so it is always
 * on screen. JB's first run: "There are a lot of questions and no UI indicating
 * progress and how much left to complete." There was — a 10px "Q9 of 22" in the
 * header corner that scrolled away. This is the fix.
 */
export function DiagnosticProgress() {
  const questionIndex = useDiagnosticStore((s) => s.currentQuestionIndex);
  const mode = useDiagnosticStore((s) => s.mode);
  const status = useDiagnosticStore((s) => s.status);
  const total = anchorItemsForMode(mode).length;
  const done = status === 'complete' ? total : Math.min(questionIndex, total);
  const pct = total > 0 ? (done / total) * 100 : 0;
  const remaining = total - done;

  return (
    <div className="px-5 py-2.5 border-b border-dark-border bg-dark-surface/70">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-body text-content">
          {done >= total ? 'All questions answered' : `Question ${Math.min(done + 1, total)} of ${total}`}
        </span>
        <span className="text-xs font-body text-content-dim">{minutesLeft(remaining)}</span>
      </div>
      <div className="w-full h-1.5 rounded-pill bg-dark-subtle overflow-hidden">
        <motion.div
          className="h-full rounded-pill bg-gradient-to-r from-indigo-500 to-gold-500"
          initial={false}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />
      </div>
    </div>
  );
}
